/** @format */

import { useEffect, useRef } from "react";

interface InfiniteScrollSentinelProps {
  /**
   * Whether the sentinel should fire `onLoadMore` when intersecting.
   * Set to `false` when there are no more pages OR a fetch is already
   * in flight — prevents duplicate triggers.
   */
  enabled: boolean;
  onLoadMore: () => void;
  /**
   * Optional scroll container — when omitted the observer uses the
   * viewport. Pass the column's scroll element on the board layout so
   * the trigger fires when scrolling inside the column, not the page.
   */
  root?: Element | null;
  /** CSS rootMargin — bumps the trigger so loading starts before reaching the end. */
  rootMargin?: string;
}

/**
 * IntersectionObserver-driven sentinel that calls `onLoadMore` whenever
 * a 1px-tall element enters the viewport (or the provided `root`).
 * Drop one at the bottom of any task list to wire up infinite scroll.
 */
export function InfiniteScrollSentinel({
  enabled,
  onLoadMore,
  root,
  rootMargin = "200px",
}: InfiniteScrollSentinelProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled || !ref.current) return;
    const target = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) onLoadMore();
      },
      { root: root ?? null, rootMargin, threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [enabled, onLoadMore, root, rootMargin]);

  return <div ref={ref} aria-hidden className="h-px w-full" />;
}
